import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { BackendServiceInterface } from './backend.service.interface';
import { FeatureType, Feedback, FeedbackCreate, FeedbackOverview } from '@shared/interfaces/feedback';
import { AnalyticsData } from '@shared/interfaces/analytics';
import { User } from '@shared/interfaces/user';
import { ChatSummary, Chat } from '@shared/interfaces/chat';
import { TelemetryData, TelemetryFilterService, TelemetryService } from '@shared/interfaces/telemetry';
import { MetricFilter } from '../../types/Metrics';

@Injectable({ providedIn: 'root' })
export class BackendService implements BackendServiceInterface {
  private httpClient = inject(HttpClient);

  getHealth(): Observable<string> {
    return this.httpClient.get<string>(`${environment.backend}/health`);
  }

  getUser(): Observable<User> {
    return this.httpClient.get<User>(`${environment.backend}/user`);
  }

  putUser(user: User): Observable<User> {
    return this.httpClient.put<User>(`${environment.backend}/user`, user);
  }

  getAnalytics(period: number): Observable<AnalyticsData> {
    return this.httpClient.get<AnalyticsData>(`${environment.backend}/analytics/${period}`);
  }

  getFeedback(): Observable<Feedback[]> {
    return this.httpClient.get<Feedback[]>(`${environment.backend}/feedback`);
  }

  getFeedbackOverview(): Observable<FeedbackOverview> {
    return this.httpClient.get<FeedbackOverview>(`${environment.backend}/feedback/overview`);
  }

  getFeedbackByFeature(feature: FeatureType): Observable<Feedback[]> {
    return this.httpClient.get<Feedback[]>(`${environment.backend}/feedback/feature/${feature}`);
  }

  postFeedback(data: FeedbackCreate): Observable<Feedback> {
    return this.httpClient.post<Feedback>(`${environment.backend}/feedback`, data);
  }

  getCustomerChats(): Observable<ChatSummary[]> {
    return this.httpClient.get<ChatSummary[]>(`${environment.backend}/customer-feedback`);
  }

  getCustomerChat(phone: number): Observable<Chat> {
    return this.httpClient.get<Chat>(`${environment.backend}/customer-feedback/${phone}`);
  }

  postGqsNumber(phone: number, gqsNumber: string): Observable<Chat> {
    return this.httpClient.post<Chat>(`${environment.backend}/customer-feedback/${phone}`, { gqsNumber });
  }

  postMessage(phone: number, message: string): Observable<Chat> {
    return this.httpClient.post<Chat>(`${environment.backend}/customer-feedback/${phone}/message`, { message });
  }

  postMessageTemplate(phone: number): Observable<Chat> {
    return this.httpClient.post<Chat>(`${environment.backend}/customer-feedback/${phone}/message/template`, {});
  }

  getTelemetryOverview(): Observable<TelemetryData> {
    return this.httpClient.get<TelemetryData>(`${environment.backend}/telemetry/services`);
  }

  getTelemetry(id: string, filters?: MetricFilter[]): Observable<TelemetryService> {
    let params = new HttpParams();

    filters?.forEach((filter) => {
      if (filter.type === 'range') {
        params = params.append(filter.name, `>${filter.selected.min}`);
        params = params.append(filter.name, `<=${filter.selected.max}`);
      } else if (filter.type === 'enumeration') {
        if (filter.selected !== 'All') {
          params = params.append(filter.name, filter.selected);
        }
      }
    });

    return this.httpClient.get<TelemetryService>(`${environment.backend}/telemetry/${id}`, { params });
  }

  getTelemetryFilters(id: string): Observable<TelemetryFilterService> {
    return this.httpClient.get<TelemetryFilterService>(`${environment.backend}/telemetry/${id}/filter`);
  }
}
